import {filter, first} from 'rxjs/operators';
import {Injectable, NgZone} from '@angular/core';
import {MatDialog, MatDialogRef, MatDialogConfig} from '@angular/material/dialog';
import {Observable, of} from 'rxjs';
import {ComponentType} from '@angular/cdk/portal';
import {
  ChangeImageDialogComponent,
  ConfirmModalDialogComponent,
  ModalOptionsInterface,
  TwoFaRequiredDialogComponent
} from '@q9elements/ui-kit/components';
import {ErrorDialogComponent} from '../../editor-interface/dialogs/error-dialog/error-dialog.component';
import {ActivityRenumberDialogComponent} from '../../editor-interface/dialogs/activity-renumber-dialog/activity-renumber-dialog.component';
import {ConfirmDialogComponent} from '../../editor-interface/dialogs/confirm-dialog/confirm-dialog.component';
import {
  ConfirmDeleteDialogComponent,
  DialogType
} from '../../editor-interface/dialogs/confirm-delete-dialog/confirm-delete-dialog.component';
import {AttachUrlDialogComponent} from '../../editor-interface/dialogs/attach-url-dialog/attach-url-dialog.component';
import {ConfirmPrintDialogComponent} from '../../editor-interface/dialogs/confirm-print-dialog/confirm-print-dialog.component';
import {MapTreeDialogComponent} from '../../editor-interface/dialogs/map-tree-dialog/map-tree-dialog.component';
import {LegendItemTextDialogComponent} from '../../editor-interface/dialogs/legend-item-text-dialog/legend-item-text-dialog.component';
import {FilterNotesDialogComponent} from '../../editor-interface/dialogs/filter-notes-dialog/filter-notes-dialog.component';
import {HeaderColorPickerComponent} from '../../editor-interface/dialogs/header-color-picker/header-color-picker.component';
import {AgentforceDialogComponent} from '../../editor-interface/dialogs/agentforce-dialog/agentforce-dialog.component';
import {LegendItemDialogPayload} from 'src/app/editor-interface/dialogs/legend-item-text-dialog/models/legend-item-dialog-payload.interface';
import {MxCell} from 'src/app/editor-interface/shared/models/mx-cell.interface';
import {
  ChangeImageDialogData,
  CreditsService,
  DialogWidth,
  PermissionsService,
  SessionService,
  SnackBarService,
  ThemePalette
} from '@q9elements/ui-kit/common';
import {GraphExportService} from 'src/app/diagram-interaction/services/graph-export.service';
import {PrintCallbackOptions} from 'src/app/editor-interface/dialogs/confirm-print-dialog/models/confirm-print-dialog-data.interface';
import {ChangeMapOwnerDialogComponent, EditOwnerDialogData} from '@q9elements/ui-kit/maps';
import {GenerateRequirementsDialogComponent} from '../../editor-interface/dialogs/generate-requirements-dialog/generate-requirements-dialog.component';
import {ActivityReorderDialogComponent} from '../../editor-interface/dialogs/activity-reorder-dialog/activity-reorder-dialog.component';
import {CreditWarningDialogComponent} from '../../editor-interface/dialogs/credit-warning-dialog/credit-warning-dialog.component';
import {CreditWarningDialogData} from '../../editor-interface/dialogs/credit-warning-dialog/models/credit-warning-dialog-data.interface';
import {get} from 'lodash';

declare const mxClient;

@Injectable({
  providedIn: 'root'
})
export class PopupManagerService {
  constructor(
    private dialogSvc: MatDialog,
    private zone: NgZone,
    private snackBarSvc: SnackBarService,
    private creditsService: CreditsService,
    private sessionService: SessionService,
    private permissionsService: PermissionsService
  ) {}

  treeDialogRef;
  agentforceDialogref;
  filterNotesDialogRef;
  sizes: any = {};
  positions: any = {};
  isIE = mxClient.IS_IE || mxClient.IS_IE11;

  public open<T = any, D = any, R = any>(
    component: ComponentType<T>,
    config?: MatDialogConfig<D>
  ): MatDialogRef<T, R> {
    return this.zone.run(() =>
      this.dialogSvc.open(component, {...config, enterAnimationDuration: '0ms'})
    );
  }

  closeAll(force?) {
    if (force) {
      return this.dialogSvc.closeAll();
    }

    this.dialogSvc.openDialogs.forEach(ref => {
      if (ref.id === 'attach-note' && !ref.componentInstance.noteAttachmentForm.invalid) {
        return ref.componentInstance.addAttachment(); // emergency save
      }

      if (ref.id === 'attach-rmn' && !ref.componentInstance.rmnLinkAttachmentForm.invalid) {
        return ref.componentInstance.updateRMNLink(); // emergency save
      }

      if (ref.id !== 'map-tree' && ref.id !== 'filter-notes') {
        ref.close();
      }
    });
  }

  isEmptyOrOnly(id) {
    const dialogs = this.dialogSvc.openDialogs;

    return dialogs.length === 0 || (dialogs.length === 1 && dialogs[0].id === id);
  }

  openActivityRenumberDialog(cellId) {
    this.open(ActivityRenumberDialogComponent, {
      panelClass: ['q9-dialog'],
      data: cellId
    })
      .afterClosed()
      .subscribe();
  }

  openLegendItemTextDialog(
    data?: LegendItemDialogPayload
  ): Observable<{text: string; description: string}> {
    return this.open(LegendItemTextDialogComponent, {
      width: DialogWidth.SM,
      panelClass: ['q9-dialog'],
      data
    }).afterClosed();
  }

  openChangeLogoDialog(data: Partial<ChangeImageDialogData>): Observable<any> {
    return this.open(ChangeImageDialogComponent, {
      width: DialogWidth.MD,
      panelClass: ['q9-dialog'],
      data
    }).afterClosed();
  }

  openHeaderColorDialog(color: string, id: string): Observable<string> {
    if (this.isDialogOpen(id)) {
      return of(null);
    }

    return this.open(HeaderColorPickerComponent, {
      id,
      panelClass: ['q9-dialog', 'q9-dialog-draggable', 'q9-header-color'],
      width: DialogWidth.SM,
      hasBackdrop: false,
      data: {previousColor: color}
    }).afterClosed();
  }

  openConfirmDialog(context, callbackOnConfirm, type, args?) {
    this.open(ConfirmDialogComponent, {
      panelClass: ['q9-dialog'],
      width: DialogWidth.SM,
      data: {
        callbackOnConfirm: callbackOnConfirm.bind(context),
        type: type,
        args: args
      }
    })
      .afterClosed()
      .subscribe(success => {
        if (success) {
          this.snackBarSvc.success(success);
        }
      });
  }

  openConfirmDeleteDialog(type: DialogType[]) {
    return this.open(ConfirmDeleteDialogComponent, {
      panelClass: ['q9-dialog', 'q9-dialog-danger'],
      data: type
    });
  }

  openErrorDialog(info: PopupErrorInfo) {
    const dialog = this.open(ErrorDialogComponent, {
      panelClass: ['q9-dialog', 'q9-dialog-danger'],
      disableClose: !!info.disableClose,
      data: info
    });

    dialog
      .afterClosed()
      .pipe(filter(cb => cb))
      .subscribe(callback => callback());

    return dialog;
  }

  openTwoFaRequiredDialog(gracePeriodExpired = false) {
    return this.open(TwoFaRequiredDialogComponent, {
      width: DialogWidth.SM,
      disableClose: true,
      data: {gracePeriodExpired}
    }).afterClosed();
  }

  openAttachURLDialog(cell: MxCell) {
    return this.open(AttachUrlDialogComponent, {
      width: DialogWidth.MD,
      panelClass: ['q9-dialog'],
      data: cell
    });
  }

  openConfirmPrintDialog(
    context: GraphExportService,
    callback: (opts: PrintCallbackOptions) => void
  ) {
    return this.open(ConfirmPrintDialogComponent, {
      width: DialogWidth.SM,
      panelClass: ['q9-dialog'],
      data: {
        context: context,
        callback: callback
      }
    });
  }

  openGenerateRequirementsDialog() {
    return this.open(GenerateRequirementsDialogComponent, {
      panelClass: ['q9-dialog'],
      width: DialogWidth.MD
    }).afterClosed();
  }

  toggleMapTree() {
    if (this.treeDialogRef) {
      this.treeDialogRef.close();
    } else {
      const panelClassArray = ['q9-dialog', 'q9-dialog-draggable', 'q9-map-tree'];

      if (this.isIE) {
        panelClassArray.push('ie');
      }

      this.treeDialogRef = this.open(MapTreeDialogComponent, {
        id: 'map-tree',
        hasBackdrop: false,
        maxWidth: '80%',
        minWidth: '400px',
        minHeight: '500px',
        panelClass: panelClassArray
      });
      this.treeDialogRef
        .afterClosed()
        .pipe(first())
        .subscribe(() => (this.treeDialogRef = void 0));
    }
  }

  openAgentforceDialog(data) {
    if (this.agentforceDialogref) {
      this.agentforceDialogref.close();
      this.agentforceDialogref
        .afterClosed()
        .pipe(first())
        .subscribe(() => {
          this.openAgentforceDialog(data);
        });

      return;
    }

    const panelClassArray = [
      'q9-dialog',
      'q9-dialog-draggable',
      'q9-dialog-resizable',
      'agentforce'
    ];

    this.agentforceDialogref = this.open(AgentforceDialogComponent, {
      data,
      id: 'agentforce',
      hasBackdrop: false,
      width: DialogWidth.MD,
      minWidth: '500px',
      minHeight: '500px',
      panelClass: panelClassArray
    });
    this.agentforceDialogref
      .afterClosed()
      .pipe(first())
      .subscribe(() => (this.agentforceDialogref = void 0));
  }

  openFilterNotesDialog(filterProp: string) {
    const id = 'filter-notes-user';

    this.filterNotesDialogRef = this.open(FilterNotesDialogComponent, {
      id: id,
      panelClass: ['q9-dialog', 'q9-dialog-draggable', 'q9-filter-notes'],
      minWidth: DialogWidth.XS,
      hasBackdrop: false,
      data: {filterProp}
    });
    this.filterNotesDialogRef
      .afterClosed()
      .pipe(first())
      .subscribe(() => (this.filterNotesDialogRef = void 0));
  }

  openConfirmPopup(payload: Partial<ModalOptionsInterface>): Observable<boolean> {
    return this.open(ConfirmModalDialogComponent, {
      panelClass: ['q9-dialog', 'q9-dialog-danger'],
      width: DialogWidth.SM,
      data: {
        ...payload,
        color: ThemePalette.WARN
      }
    }).afterClosed();
  }

  public isDialogOpen(id: string): boolean {
    return !!this.dialogSvc.getDialogById(id);
  }

  openEditOwnershipPopup(dialogData: Partial<EditOwnerDialogData>) {
    return this.open(ChangeMapOwnerDialogComponent, {
      width: DialogWidth.SM,
      data: {
        color: ThemePalette.PRIMARY,
        ...dialogData
      }
    })
      .afterClosed()
      .pipe(filter(Boolean));
  }

  openActivityReorderDialog(id: string) {
    if (this.isDialogOpen(id)) {
      return of(null);
    }

    return this.open(ActivityReorderDialogComponent, {
      panelClass: ['q9-dialog', 'q9-dialog-draggable', 'q9-drag-anchor'],
      width: DialogWidth.SM,
      hasBackdrop: false,
      id
    }).afterClosed();
  }

  openCreditWarningDialog(data: CreditWarningDialogData): Observable<boolean> {
    const isFree = this.permissionsService.isFree();
    const user = this.sessionService.user();
    const hasSubscription = !!get(user, 'userInTeam.team.subscription.subscriptionId');

    if (!(isFree || hasSubscription)) {
      return of(true);
    }

    return this.open(CreditWarningDialogComponent, {
      panelClass: ['q9-dialog'],
      width: DialogWidth.MD,
      data
    }).afterClosed();
  }
}

export interface PopupErrorInfo {
  message;
  callback?;
  disableClose?: boolean;
  additionalMessage?;
  navWithoutReload?: boolean;
}
